import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Divider from "@mui/material/Divider";
import Button from "@mui/material/Button";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import { alpha } from "@mui/material/styles";

// A single discount line: a long voucher/package name truncates with an ellipsis
// so the amount stays aligned on the right instead of wrapping under the label.
function ReductionRow({ label, amount, displayCurrency }) {
  return (
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 1, color: "primary.main" }}>
      <Typography
        variant="body2"
        title={label}
        sx={{ color: "primary.main", fontWeight: 500, flex: 1, minWidth: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
      >
        {label}
      </Typography>
      <Typography variant="body2" sx={{ color: "primary.main", fontWeight: 500, flexShrink: 0 }}>
        −{displayCurrency}
        {amount.toFixed(2)}
      </Typography>
    </Box>
  );
}

function CheckoutOrderSummary({
  displayCurrency,
  primaryItem,
  membershipApplies,
  membershipSaving,
  isPackageApplied,
  packageSessionValue,
  voucherUsed,
  selectedVoucher,
  total,
  // Split bookings authorize a hold rather than take payment, so the summary
  // says what the number actually is. Sub-label carries the per-player share.
  totalLabel = "Total",
  totalSubLabel = "",
  error,
  submitting,
  onCheckout,
  onClearCart,
  hideCta = false,
  hideTotal = false,
  hideClearCart = false,
  canSubmit = true,
  creditsSlot,
  // The split-booking notice draws its own tinted box, so it needs no card
  // around it when it ends up alone. The credits panel is a plain section and
  // does.
  creditsSlotSelfContained = false,
  termsSlot
}) {
  const hasReduction = membershipApplies || isPackageApplied || voucherUsed > 0;
  // A session paid for with a pack is shown at the pack's own rate, matching
  // both the saving the client was promised when they bought it and the figure
  // the club's booking log records.
  const subtotal = isPackageApplied ? packageSessionValue : parseFloat(primaryItem?.price) || 0;

  const showTotals = !hideTotal || hasReduction;
  const showFooter = !hideCta || !hideClearCart;

  // Mobile with no credits to apply and no discount line: the sticky bar owns
  // the total and the view owns clear-cart, so the card has nothing left to say.
  if (!creditsSlot && !showTotals && !showFooter && !error) return null;

  // Only the credits slot survived (mobile: the sticky bar took the total and
  // the CTA). A self-contained slot renders bare — card chrome around a notice
  // that is already a box is a box drawn around a box. Anything else keeps the
  // card, otherwise the section floats borderless until a credit is picked and
  // the totals bring the card back.
  const isSlotOnly = !!creditsSlot && !showTotals && !showFooter && !error;
  if (isSlotOnly && creditsSlotSelfContained) return creditsSlot;

  return (
    <Paper
      elevation={0}
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        borderRadius: 3,
        border: 1,
        borderColor: (theme) => alpha(theme.palette.divider, 0.6),
        backgroundColor: "background.paper",
        overflow: "hidden"
      }}
    >
      {creditsSlot}
      {creditsSlot && showTotals && <Divider />}

      {/* hideTotal: on mobile the sticky bottom bar already shows the Total, so the
          duplicate Total line here is dropped. Any discount breakdown still shows. */}
      {showTotals && (
        <Stack spacing={1.25} sx={{ p: 3 }}>
          {hasReduction && (
            <Box sx={{ display: "flex", justifyContent: "space-between", color: "text.secondary" }}>
              <Typography variant="body2">Subtotal</Typography>
              <Typography variant="body2">
                {displayCurrency}
                {subtotal.toFixed(2)}
              </Typography>
            </Box>
          )}

          {membershipApplies && <ReductionRow label="Member pricing" amount={membershipSaving} displayCurrency={displayCurrency} />}

          {isPackageApplied && <ReductionRow label="Package — 1 session" amount={subtotal} displayCurrency={displayCurrency} />}

          {voucherUsed > 0 && (
            <ReductionRow
              label={`Voucher${selectedVoucher?.name ? ` — ${selectedVoucher.name}` : ""}`}
              amount={voucherUsed}
              displayCurrency={displayCurrency}
            />
          )}

          {hasReduction && !hideTotal && <Divider sx={{ my: 1, opacity: 0.5 }} />}

          {!hideTotal && (
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2 }}>
              <Box>
                <Typography sx={{ fontSize: "1.125rem", fontWeight: 600 }}>{totalLabel}</Typography>
                {totalSubLabel ? (
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.4, mt: 0.25 }}>
                    {totalSubLabel}
                  </Typography>
                ) : null}
              </Box>
              <Typography sx={{ fontSize: "1.5rem", fontWeight: 800, letterSpacing: "-0.01em", whiteSpace: "nowrap" }}>
                {displayCurrency}
                {total.toFixed(2)}
              </Typography>
            </Box>
          )}
        </Stack>
      )}

      {error && (
        <Alert severity="error" sx={{ mx: 3, mb: 2, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {/* hideCta / hideClearCart: on mobile the sticky bottom bar carries the
          primary action and the view renders its own clear-cart button, so this
          footer would be an empty tinted strip under the credits panel. */}
      {showFooter && (
        <Stack
          spacing={1}
          sx={{
            p: 3,
            pt: 2,
            backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.04)
          }}
        >
          {!hideCta && termsSlot}
          {!hideCta && (
            <Button
              variant="contained"
              onClick={onCheckout}
              disabled={submitting || !canSubmit}
              fullWidth
              startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
              sx={{
                py: 1.25,
                borderRadius: 2,
                fontSize: "0.875rem",
                fontWeight: 700,
                boxShadow: (theme) => `0 6px 18px ${alpha(theme.palette.primary.main, 0.22)}`
              }}
            >
              {submitting ? "Processing..." : "Complete Booking"}
            </Button>
          )}
          {!hideClearCart && (
            <Button
              onClick={onClearCart}
              disabled={submitting}
              size="small"
              sx={{
                alignSelf: "center",
                color: "error.main",
                fontSize: "0.75rem",
                fontWeight: 600,
                textTransform: "none",
                "&:hover": { backgroundColor: "transparent", color: "error.dark", textDecoration: "underline" }
              }}
            >
              Clear cart
            </Button>
          )}
        </Stack>
      )}
    </Paper>
  );
}

export default CheckoutOrderSummary;
